import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { IoIosArrowForward } from "react-icons/io";
import { ImageComponent } from "../../global/imageComponent";
import { useDispatch } from "react-redux";
import { SELECT_SPORT } from "../../constants";
import { getGamedatas } from "../../actions/gameSettings";
import SportIcon from "../../common/SportIcon";
import { isDTS } from "../../../utility/platform";
import { getEntryTypeLabel, getTerm } from "../../../utility/complianceCopy";
import { useComplianceConfig } from "../../../utility/useComplianceConfig";
import {
  ROUTES,
  ALL_SPORTS_KEY,
  LIVE_BOARD_KEY,
  allSportsPath,
  favoritesBoardPath,
  liveBoardPath,
  sportBoardPath,
  boardSportFromPath,
  isRoutePath,
} from "../../../constants/routes";
import { isFavoritesBoardFilter } from "../../../utility/favoritesRecents";
import { filterNavItems } from "../../../utility/phoneShell";

function Sidebar({ sportFilter = "", onNavigate } = {}) {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [activeSub, setActiveSub] = useState(null); // track active submenu
  const [gameDate, setGameData] = useState([]); // fetched sports data

  // fetch game data from backend
  const fetchGameData = async () => {
    try {
      const { status, message, result } = await getGamedatas(dispatch);
      console.log("fetchGameData ~ status, message, result:", status, message, result);
      if (status === true && Array.isArray(result)) {
        setGameData(result);
      }
    } catch (err) {
      console.log("err-fetch", err);
    }
  };

  // base sidebar items
  const baseSidebarData = [
    { id: 101, labelText: "Home", navLink: allSportsPath(), isActive: false, iconSrc: "homeIcon" },
    {
      id: 102,
      labelText: "Favorites",
      navLink: favoritesBoardPath(),
      isActive: false,
      iconSrc: "favoritesIcon",
    },
    { id: 103, labelText: "Live", navLink: liveBoardPath(), isActive: false, iconSrc: "liveIcon" },
    // compliance-ok: tab id arg for getEntryTypeLabel; player sees Target pick / Call Bets via that helper
    { id: 104, labelText: getEntryTypeLabel("Call Bets", isDTS()), navLink: ROUTES.callBet, isActive: false, iconSrc: "bonusIcon" },
    { id: 105, labelText: isDTS() ? `Active ${getTerm("entry", { plural: true })}` : "Active Bets", navLink: ROUTES.openBet, isActive: false, iconSrc: "betIcon" },
    {
      id: 106,
      labelText: "All Sports",
      navLink: null,
      isActive: true,
      isExpand: true,
    },
  ];

  // Site settings land after mount, so sidebarList's initial value is built from
  // the built-in defaults. Subscribing re-renders us when the real vocabulary
  // arrives; the label itself is read from baseSidebarData at render (below)
  // rather than from state, which would still hold the stale wording.
  useComplianceConfig();

  const [sidebarList, setSidebarList] = useState([...baseSidebarData]);

  // sidebarList carries the active/expand flags, but its labels are a snapshot
  // taken at mount — before site settings loaded. Always read the label from
  // the freshly-built base list so admin vocabulary shows up.
  const labelFor = (item) =>
    baseSidebarData.find((b) => b.id === item?.id)?.labelText ?? item?.labelText;

  // Update active states based on URL. Board links are matched by the sport
  // they show, not by raw path, so they stay highlighted on both the legacy and
  // the compliant path and whether or not the segment arrived URL-encoded.
  useEffect(() => {
    const currentPath = location.pathname;
    const boardSport = boardSportFromPath(currentPath);
    const favoritesOn = isFavoritesBoardFilter(location.search);
    const isSportBoard =
      Boolean(boardSport) && boardSport !== ALL_SPORTS_KEY && boardSport !== LIVE_BOARD_KEY;

    const activeById = {
      101: boardSport === ALL_SPORTS_KEY && !favoritesOn,
      102: favoritesOn || currentPath === "/favorites",
      103: boardSport === LIVE_BOARD_KEY,
      104: isRoutePath(currentPath, "callBet"),
      105: isRoutePath(currentPath, "openBet"),
      // All Sports: a specific sport's board, i.e. not Home and not Live.
      106: isSportBoard && !favoritesOn,
    };

    setSidebarList((prev) =>
      prev.map((item) => {
        const active =
          item.id in activeById
            ? activeById[item.id]
            : Boolean(item.navLink) &&
              typeof item.navLink === "string" &&
              (currentPath === item.navLink || currentPath === `${item.navLink}/`);
        return { ...item, isActive: active };
      })
    );

    // Favorites lives on the all-sports URL, so boardSport is A-Z — do not
    // treat A-Z Play as the active submenu while the Favorites filter is on.
    setActiveSub(
      favoritesOn || boardSport === null
        ? null
        : location.state?.selectedSport || boardSport
    );
  }, [location]);

  // Fetch game data on mount
  useEffect(() => {
    fetchGameData();
  }, []);

  // toggle sidebar parent
  const sidebarClickHandle = (item) => {
    if (item.navLink) {
      if (item.id === 102) {
        dispatch({ type: SELECT_SPORT, data: ALL_SPORTS_KEY });
        history.push(favoritesBoardPath());
        onNavigate?.();
        return;
      }
      if (item.id === 103) {
        dispatch({ type: SELECT_SPORT, data: LIVE_BOARD_KEY });
        history.push({
          pathname: liveBoardPath(),
          state: { selectedSport: LIVE_BOARD_KEY },
        });
        onNavigate?.();
        return;
      }
      history.push(item.navLink);
      onNavigate?.();
    } else {
      setSidebarList((prev) =>
        prev.map((it) =>
          it.id === item.id ? { ...it, isExpand: !it.isExpand } : { ...it, isExpand: false }
        )
      );
    }
  };

  // click on submenu item. Navigate by the sport id, never the label: on DTS
  // the all-sports label is reworded, and sending that wording as the sport
  // would ask the odds API for a sport that does not exist.
  const subMenuClickHandle = (subItem) => {
    if (subItem.navLink) {
      dispatch({ type: SELECT_SPORT, data: subItem.sportKey });
      history.push({
        pathname: sportBoardPath(subItem.sportKey),
        // Drop Favorites/Live query filters when leaving for a sport board.
        search: "",
        state: { selectedSport: subItem.sportKey },
      });
      onNavigate?.();
    }
  };

  // dynamically add subMenu to All Sports
  // Inside your Sidebar component, replace the dynamicSidebarList creation with this:

  const dynamicSidebarList = sidebarList.map((item) => {
    if (item.id === 106) {
      // Default submenu item
      const defaultSub = {
        subId: 1,
        sportKey: ALL_SPORTS_KEY,
        labelText: isDTS() ? `A-Z ${getTerm("play")}` : "A-Z Betting",
        navLink: ROUTES.straightBet,
        isActive: false,
        iconSrc: "aTozIcon",
      };

      // Map dynamic fetched sports
      const dynamicSubs = gameDate.map((sport, index) => {
        const label = sport._id || sport.name || `Sport ${index + 2}`; // +2 because 1 is default
        return {
          subId: index + 2, // start after default
          sportKey: label,
          labelText: label,
          navLink: ROUTES.straightBet,
          iconUrl: sport.iconUrl || null,
        };
      });

      return {
        ...item,
        subMenu: [defaultSub, ...dynamicSubs], // merge default + dynamic
      };
    }
    return item;
  });


  const visibleSidebarList = filterNavItems(dynamicSidebarList, sportFilter, labelFor);

  return (
    <div className="mg-sidebar-component">
      <ul>
        {visibleSidebarList.map((item) => (
          <li key={item.id}>
            <div
              className={`sidebarListHeader ${item?.isActive ? "isActive" : ""}`}
              onClick={() => sidebarClickHandle(item)}
            >
              <div className="d-flex align-items-center gap-2">
                <ImageComponent staticImgSrc={item?.iconSrc} />
                <h3 className="fs15">{labelFor(item)}</h3>
              </div>
              {item.subMenu && (
                <button className="arrowIcon">
                  <IoIosArrowForward />
                </button>
              )}
            </div>

            {item?.isExpand && item?.subMenu && (
              <div className="sidebarListBody">
                <ul>
                  {item?.subMenu.map((subItem) => (
                    <li
                      key={subItem?.subId}
                      className={`sidebarListHeader ${activeSub === subItem?.sportKey ? "isActive" : ""}`}
                      onClick={() => subMenuClickHandle(subItem)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        {subItem?.iconSrc ? (
                          <ImageComponent staticImgSrc={subItem.iconSrc} />
                        ) : (
                          <SportIcon
                            sportName={subItem?.labelText}
                            iconUrl={subItem?.iconUrl}
                            size={22}
                          />
                        )}
                        <h3 className="fs14">{subItem?.labelText}</h3>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Sidebar;
